/**
 * SocialProposalView — public /proposal/{slug} page for social_pitch proposals.
 * Example video carousel first, bespoke pricing table below.
 * Custom agent-branded examples override the defaults when enabled.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import PricingTable from '@/PricingTable';
import ProductShowcase from '@/ProductShowcase';
import OverviewAudio from '@/OverviewAudio';
import CustomerJourney from '@/CustomerJourney';
import { applyCustomExamples } from '@/exampleVideos';
import FloatingNav from '@/FloatingNav';
import './ShowcaseLanding.css';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://nichecomfiles.co.uk/proposal-examples/defaults/nichecom-logo-trimmed.png';

const NAV_ITEMS = [
  { key: 'examples', label: 'Examples', selector: '[data-testid="sl-showcase"]' },
  { key: 'pricing', label: 'Pricing', selector: '[data-testid="lp-packages"]' },
  { key: 'journey', label: 'Customer Journey', selector: '[data-testid="customer-journey"]' },
];

const INFO_PATHS = { presentour: '/info/presentour', narratour: '/info/narratour', socialweek: '/info/social-week' };

export default function SocialProposalView({ proposal }) {
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/example-videos/public`)
      .then((r) => setVideos(applyCustomExamples(r.data, proposal)))
      .catch(() => setVideos({ introductions: [], examples: [] }));
  }, [proposal]);

  return (
    <div className="sl sl-proposal" data-testid="social-proposal-view">
      <FloatingNav items={NAV_ITEMS} />
      <header className="sl-header" data-testid="sl-header">
        <img src={LOGO} alt="NicheCom" className="sl-logo" />
        <div className="sl-proposal-badge" data-testid="sl-proposal-badge">
          <span className="sl-proposal-badge-eyebrow">Proposal for</span>
          <span className="sl-proposal-badge-name" data-testid="sl-proposal-agency">{proposal.agency_name}</span>
        </div>
      </header>

      {/* Carousel is the hero: fills the first viewport, centred */}
      <section className="sl-showcase sl-showcase-hero" data-testid="sl-showcase">
        <ProductShowcase videos={videos || { introductions: [], examples: [] }} />
      </section>
      <OverviewAudio />

      <PricingTable
        proposal={proposal}
        onNavigate={(page) => { const path = INFO_PATHS[page]; if (path) window.location.href = path; }}
      />

      {proposal.rd_contact && (
        <section className="rdcard-wrap" data-testid="rd-contact-card">
          <span className="sl-eyebrow">Your NicheCom contact</span>
          <div className="rdcard">
            <img src={proposal.rd_contact.photo_url} alt={proposal.rd_contact.name} className="rdcard-photo" data-testid="rd-contact-photo" />
            <div className="rdcard-info">
              <h3 className="rdcard-name" data-testid="rd-contact-name">{proposal.rd_contact.name}</h3>
              <span className="rdcard-title">{proposal.rd_contact.title}</span>
              <div className="rdcard-links">
                <a href={`mailto:${proposal.rd_contact.email}`} className="rdcard-link" data-testid="rd-contact-email">{proposal.rd_contact.email}</a>
                <a href={`tel:${proposal.rd_contact.phone.replace(/\s/g, '')}`} className="rdcard-link" data-testid="rd-contact-phone">{proposal.rd_contact.phone}</a>
              </div>
            </div>
          </div>
        </section>
      )}

      <CustomerJourney videos={videos || { introductions: [], examples: [] }} />

      <footer className="sl-footer" data-testid="sl-footer">
        <img src={LOGO} alt="NicheCom" className="sl-footer-logo" />
        <span>© {new Date().getFullYear()} NicheCom. All rights reserved.</span>
      </footer>
    </div>
  );
}
